import type {SyncConfigComponentProps} from '@cohort/merchants/apps';
import {useBrevoContactAttributes} from '@cohort/merchants/apps/brevo/actions';
import ImportUsersFields from '@cohort/merchants/apps/brevo/sync/ImportUsersFields';
import {RadioCards} from '@cohort/merchants/components/form/RadioCards';
import HighlightText from '@cohort/merchants/components/HighlightText';
import LoadingState from '@cohort/merchants/components/LoadingState';
import type FeatureFlags from '@cohort/merchants/featureFlags';
import {useCurrentMerchant} from '@cohort/merchants/hooks/contexts/currentMerchant';
import {useCohortForm} from '@cohort/merchants/hooks/contexts/form';
import type {SyncConfigFormValues} from '@cohort/merchants/pages/apps/app/utils';
import {useFlags} from 'launchdarkly-react-client-sdk';
import {useTranslation} from 'react-i18next';

type ImportUsersConfigProps = SyncConfigComponentProps;
const ImportUsersConfig: React.FC<ImportUsersConfigProps> = ({connection}) => {
  const merchant = useCurrentMerchant();

  const {setValue, register, control, watch} =
    useCohortForm<Extract<SyncConfigFormValues, {appId: 'brevo'}>>();

  const {t} = useTranslation('app-brevo', {
    keyPrefix: 'sync.importUsersConfig',
  });

  const {data: getContactAttributesResponse, isFetched: isBrevoContactAttributesFetched} =
    useBrevoContactAttributes(merchant.id, connection.id);
  const brevoContactAttributes = getContactAttributesResponse?.output.attributes ?? [];

  const isImportEnabled = watch('userImportEnabled');
  const {featureImportAllBrevoContacts} = useFlags<FeatureFlags>();

  if (!isBrevoContactAttributesFetched) {
    return <LoadingState className="bg-white" />;
  }

  return (
    <div className="flex flex-col space-y-4">
      <RadioCards
        name="userSyncConfig.createUsers"
        label={t('labelUserGroup')}
        register={register}
        control={control}
        disabled={!isImportEnabled}
        options={[
          {label: t('labelExistingUsers'), value: 'false'},
          {label: t('labelAllUsers'), value: 'true', disabled: !featureImportAllBrevoContacts},
        ]}
        onChange={e => {
          setValue('userSyncConfig.createUsers', e.target.value === 'true');
        }}
        preventFieldOnChange
      />
      {!featureImportAllBrevoContacts && isImportEnabled && (
        <HighlightText text={t('highlightTextImportAllBrevoContactsDisabled')} type="info" />
      )}
      <ImportUsersFields brevoContactAttributes={brevoContactAttributes} />
    </div>
  );
};

export default ImportUsersConfig;
